import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchProjectSettings,
  updateProjectSettings,
  SettingsConflictError,
} from '../../lib/api/projects';
import type { ProjectSettingsView, ProjectSettings } from '../../../../shared/types/projects';
import { PLANS } from '../../../../shared/types/plans';
import { useProjectPlan } from '../../lib/hooks/useProjectPlan';
import {
  validateCustomSteps,
  type CustomAgentDef,
  type WorkflowCustomStep,
} from '../../../../shared/lib/customSteps';

/**
 * FORGE-161: visual custom-workflow editor (completes FORGE-068 — the Sola-style
 * differentiator). Renders the story pipeline as a vertical track: built-in
 * stages in indigo, the operator's custom agents inserted inline in violet at
 * their trigger point. Each custom node opens an inline "configure" panel.
 * Reads/writes the project SETTINGS row's `custom_agents` + `workflow.custom_steps`
 * — the same surfaces the orchestrator overlays in loadRepoConfig — and validates
 * with the orchestrator's mirrored rules so a config that saves is one that runs.
 */

// The canonical per-story pipeline, split into a linear spine + a parallel
// implementation fan. `trigger` is the stage key a custom step can attach AFTER
// (null = no insertion point there). Mirrors the cross-repo handler: the impl
// agents (backend/web/mobile) run concurrently after tests, then the pipeline
// joins back for review/ship.
interface Stage { key: string; label: string; sub: string; trigger: string | null; }
const PRE: Stage[] = [
  { key: 'solution-architect', label: 'Plan',        sub: 'solution-architect', trigger: null },
  { key: 'qa',                 label: 'Write tests', sub: 'qa',                 trigger: 'qa' },
];
// Parallel branches — each can carry its own custom steps.
const IMPL: { key: string; label: string }[] = [
  { key: 'backend', label: 'Backend' },
  { key: 'web',     label: 'Web' },
  { key: 'mobile',  label: 'Mobile' },
];
const POST: Stage[] = [
  { key: 'review',      label: 'Review & ship', sub: 'secops · devops', trigger: 'devops' },
  { key: 'qa-validate', label: 'Validate',      sub: 'qa-validate',     trigger: null },
];

interface AgentDraft { name: string; prompt: string; model: string; maxIterations: string; blocking: boolean; }
const blankDraft = (): AgentDraft => ({ name: '', prompt: '', model: '', maxIterations: '', blocking: false });

export const WorkflowTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const settingsQ = useQuery({
    queryKey: ['project', projectId, 'settings'],
    queryFn: () => fetchProjectSettings(projectId),
    enabled: projectId.length > 0,
  });
  // Gate on the PROJECT plan via the canonical hook (never the account plan).
  const { plan } = useProjectPlan(projectId);

  if (settingsQ.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (settingsQ.isError)
    return <div role="alert" className="text-sm text-red-600">{(settingsQ.error as Error).message}</div>;
  if (!settingsQ.data) return null;

  return (
    <WorkflowEditor
      projectId={projectId}
      initial={settingsQ.data}
      planLocked={!PLANS[plan].features.feature_custom_agents}
      onSaved={() => settingsQ.refetch()}
    />
  );
};

const readAgents = (s: ProjectSettings): CustomAgentDef[] =>
  Array.isArray((s as any).custom_agents) ? ((s as any).custom_agents as CustomAgentDef[]) : [];
const readSteps = (s: ProjectSettings): WorkflowCustomStep[] => {
  const wf = (s.workflow ?? {}) as Record<string, unknown>;
  return Array.isArray(wf.custom_steps) ? (wf.custom_steps as WorkflowCustomStep[]) : [];
};

const WorkflowEditor: React.FC<{
  projectId: string;
  initial: ProjectSettingsView;
  planLocked: boolean;
  onSaved: () => void;
}> = ({ projectId, initial, planLocked, onSaved }) => {
  const [view, setView] = useState<ProjectSettingsView>(initial);
  const [agents, setAgents] = useState<CustomAgentDef[]>(() => readAgents(initial.settings));
  const [steps, setSteps] = useState<WorkflowCustomStep[]>(() => readSteps(initial.settings));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflictMsg, setConflictMsg] = useState<string | null>(null);

  // Inline editor: `{ after, stepIndex? }`. stepIndex absent → adding a new node.
  const [editing, setEditing] = useState<{ after: string; stepIndex?: number } | null>(null);
  const [draft, setDraft] = useState<AgentDraft>(blankDraft());

  const errors = useMemo(() => validateCustomSteps(agents, steps), [agents, steps]);
  const isDirty =
    JSON.stringify(agents) !== JSON.stringify(readAgents(view.settings)) ||
    JSON.stringify(steps) !== JSON.stringify(readSteps(view.settings));

  const agentByName = useMemo(() => new Map(agents.map((a) => [a.name, a])), [agents]);

  const openNew = (after: string) => {
    setDraft(blankDraft());
    setEditing({ after });
  };
  const openEdit = (after: string, stepIndex: number) => {
    const step = steps[stepIndex];
    const a = agentByName.get(step.agent);
    setDraft({
      name: step.agent,
      prompt: a?.prompt ?? '',
      model: a?.model ?? '',
      maxIterations: a?.max_iterations != null ? String(a.max_iterations) : '',
      blocking: step.blocking === true,
    });
    setEditing({ after, stepIndex });
  };
  const closeEditor = () => { setEditing(null); setDraft(blankDraft()); };

  const commitEditor = () => {
    if (!editing) return;
    const name = draft.name.trim();
    const agentDef: CustomAgentDef = {
      name,
      prompt: draft.prompt.trim(),
      ...(draft.model.trim() ? { model: draft.model.trim() } : {}),
      ...(draft.maxIterations.trim() ? { max_iterations: Number(draft.maxIterations) } : {}),
    };
    // Upsert the agent def (1:1 with this node, keyed by name).
    setAgents((prev) => {
      const others = prev.filter((a) =>
        a.name !== name &&
        (editing.stepIndex == null || a.name !== steps[editing.stepIndex!]?.agent),
      );
      return [...others, agentDef];
    });
    const stepDef: WorkflowCustomStep = { after: editing.after, agent: name, ...(draft.blocking ? { blocking: true } : {}) };
    setSteps((prev) => {
      if (editing.stepIndex == null) return [...prev, stepDef];
      return prev.map((s, i) => (i === editing.stepIndex ? stepDef : s));
    });
    closeEditor();
  };

  const removeStep = (stepIndex: number) => {
    const removed = steps[stepIndex];
    const remaining = steps.filter((_, i) => i !== stepIndex);
    setSteps(remaining);
    // Drop the agent def too if nothing else references it.
    if (removed && !remaining.some((s) => s.agent === removed.agent)) {
      setAgents((prev) => prev.filter((a) => a.name !== removed.agent));
    }
    if (editing?.stepIndex === stepIndex) closeEditor();
  };

  const handleSave = async () => {
    setSaveError(null); setConflictMsg(null);
    if (errors.length > 0) { setSaveError(errors[0]); return; }
    setSaving(true);
    try {
      const nextSettings: ProjectSettings = {
        ...view.settings,
        ...(agents.length ? { custom_agents: agents } : { custom_agents: undefined }),
        workflow: {
          ...((view.settings.workflow ?? {}) as Record<string, unknown>),
          ...(steps.length ? { custom_steps: steps } : { custom_steps: undefined }),
        },
      } as ProjectSettings;
      const newView = await updateProjectSettings(projectId, {
        settings: nextSettings,
        expectedVersion: view.version === 0 ? null : view.version,
      });
      setView(newView);
      setAgents(readAgents(newView.settings));
      setSteps(readSteps(newView.settings));
      onSaved();
    } catch (err) {
      if (err instanceof SettingsConflictError) { setConflictMsg(err.message); setView(err.current); }
      else setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const customCount = steps.length;

  // A single linear-spine stage: node card + its custom steps + inline editor +
  // insert affordance. Used for the pre (Plan/tests) and post (review/validate)
  // segments; the parallel impl branches render their own compact variant.
  const renderLinearStage = (stage: Stage, first: boolean) => (
    <React.Fragment key={stage.key}>
      <TrackNode color="indigo" connectorTop={!first}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-slate-800">{stage.label}</span>
          <span className="font-mono text-[11px] text-slate-400">{stage.sub}</span>
        </div>
      </TrackNode>
      {stage.trigger && (
        <>
          {steps.map((s, i) =>
            s.after === stage.trigger ? (
              <CustomNode
                key={`step-${i}`}
                step={s}
                agent={agentByName.get(s.agent)}
                index={i}
                disabled={planLocked}
                onEdit={() => openEdit(stage.trigger!, i)}
                onRemove={() => removeStep(i)}
              />
            ) : null,
          )}
          {editing && editing.after === stage.trigger && (
            <InlineEditor after={stage.trigger} draft={draft} setDraft={setDraft} onCancel={closeEditor} onConfirm={commitEditor} />
          )}
          {!planLocked && !(editing && editing.after === stage.trigger && editing.stepIndex == null) && (
            <InsertButton trigger={stage.trigger} onClick={() => openNew(stage.trigger!)} />
          )}
        </>
      )}
    </React.Fragment>
  );

  return (
    <div className="space-y-5">
      {/* Header --------------------------------------------------------- */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <LegendDot className="bg-indigo-500" /> <span>Built-in stage</span>
            <LegendDot className="bg-violet-500" /> <span>Custom agent</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {customCount === 0
              ? 'Your pipeline runs the built-in stages. Insert a custom agent at any ⊕ point.'
              : `${customCount} custom step${customCount !== 1 ? 's' : ''} injected into the pipeline.`}
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving || planLocked || errors.length > 0}
          data-testid="save-workflow"
          className="px-4 py-1.5 text-sm bg-primary-500 text-white rounded-md disabled:opacity-40 hover:bg-primary-600 transition-colors shrink-0"
        >
          {saving ? 'Saving…' : 'Save pipeline'}
        </button>
      </div>

      {planLocked && (
        <div role="alert" className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <span aria-hidden className="mt-0.5 shrink-0">⚠</span>
          <span>Custom workflow steps require the <strong>Enterprise plan</strong>. Upgrade this project to design your pipeline.</span>
        </div>
      )}
      {conflictMsg && <div role="alert" className="border border-yellow-300 bg-yellow-50 rounded-md p-2 text-xs text-yellow-900">{conflictMsg}</div>}
      {saveError && <div role="alert" className="border border-red-200 bg-red-50 text-red-800 text-xs rounded-md p-2">{saveError}</div>}
      {errors.length > 0 && (
        <div role="alert" data-testid="workflow-errors" className="border border-red-200 bg-red-50 text-red-800 text-xs rounded-md p-2 space-y-1">
          {errors.map((e, i) => <div key={i}>· {e}</div>)}
        </div>
      )}

      {/* Pipeline track ------------------------------------------------- */}
      <div className="relative pl-1" data-testid="pipeline-track">
        {/* Linear spine — Plan → Write tests */}
        {PRE.map((stage, si) => renderLinearStage(stage, si === 0))}

        {/* Fork into the parallel implementation fan */}
        <SpineConnector />
        <div className="ml-7 border border-slate-200 rounded-lg bg-slate-50/70 px-3 py-3" data-testid="impl-fan">
          <div className="flex items-center gap-2 mb-2">
            <i className="ti ti-arrows-split-2" aria-hidden style={{ fontSize: 14 }} />
            <span className="text-[10px] uppercase tracking-wide text-slate-500">Implement · runs in parallel</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start">
            {IMPL.map((branch) => (
              <div key={branch.key} className="flex flex-col gap-1.5" data-testid={`branch-${branch.key}`}>
                <div className="border border-slate-200 border-t-2 border-t-indigo-400 rounded-md bg-white px-2.5 py-1.5">
                  <div className="text-xs font-medium text-slate-800">{branch.label}</div>
                  <div className="font-mono text-[10px] text-slate-400">{branch.key}</div>
                </div>
                {steps.map((s, i) =>
                  s.after === branch.key ? (
                    <BranchCustomNode
                      key={`b-${i}`}
                      step={s}
                      agent={agentByName.get(s.agent)}
                      index={i}
                      disabled={planLocked}
                      onEdit={() => openEdit(branch.key, i)}
                      onRemove={() => removeStep(i)}
                    />
                  ) : null,
                )}
                {!planLocked && !(editing && editing.after === branch.key && editing.stepIndex == null) && (
                  <BranchInsert stage={branch.key} onClick={() => openNew(branch.key)} />
                )}
              </div>
            ))}
          </div>
          {editing && IMPL.some((b) => b.key === editing.after) && (
            <div className="mt-2">
              <InlineEditor after={editing.after} draft={draft} setDraft={setDraft} onCancel={closeEditor} onConfirm={commitEditor} bare />
            </div>
          )}
        </div>
        <SpineConnector />

        {/* Join — Review & ship → Validate */}
        {POST.map((stage) => renderLinearStage(stage, false))}
      </div>

      <p className="text-xs text-slate-400">
        A custom agent runs after its stage and gates the pipeline until it completes.
        A force-completed step still releases the gate, so a crashed agent can't wedge a story.
        Changes apply to the next epic (config caches ~1h).
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Track primitives
// ---------------------------------------------------------------------------

const LegendDot: React.FC<{ className: string }> = ({ className }) => (
  <span className={`inline-block w-2.5 h-2.5 rounded-full ${className}`} aria-hidden />
);

/** A built-in stage row: colored dot on the rail + a card. */
const TrackNode: React.FC<{ color: 'indigo'; connectorTop: boolean; children: React.ReactNode }> = ({
  connectorTop, children,
}) => (
  <div className="relative flex gap-3 pb-2">
    <div className="relative flex flex-col items-center w-4 shrink-0">
      {connectorTop && <span className="absolute -top-2 h-3 w-px bg-slate-200" />}
      <span className="mt-1.5 w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-indigo-50 z-10" />
      <span className="flex-1 w-px bg-slate-200" />
    </div>
    <div className="flex-1 border border-slate-200 border-l-2 border-l-indigo-400 rounded-lg bg-white px-3 py-2 -mt-0.5">
      {children}
    </div>
  </div>
);

/** A placed custom agent row (violet). */
const CustomNode: React.FC<{
  step: WorkflowCustomStep;
  agent?: CustomAgentDef;
  index: number;
  disabled: boolean;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ step, agent, index, disabled, onEdit, onRemove }) => (
  <div className="relative flex gap-3 pb-2" data-testid={`custom-node-${index}`}>
    <div className="relative flex flex-col items-center w-4 shrink-0">
      <span className="absolute -top-2 h-3.5 w-px bg-violet-200" />
      <span className="mt-1.5 w-3 h-3 rounded-full bg-violet-500 ring-4 ring-violet-50 z-10" />
      <span className="flex-1 w-px bg-slate-200" />
    </div>
    <div className="flex-1 border border-violet-200 border-l-2 border-l-violet-500 rounded-lg bg-violet-50/60 px-3 py-2 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold text-violet-900 truncate">{step.agent || '(unnamed)'}</span>
          <span className="text-[10px] uppercase tracking-wide bg-violet-100 text-violet-600 rounded px-1.5 py-0.5">after {step.after}</span>
        </div>
        <div className="text-[11px] text-slate-500 truncate font-mono">
          {agent?.prompt || 'no prompt set'}{agent?.model ? ` · ${agent.model}` : ''}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button type="button" onClick={onEdit} disabled={disabled}
          data-testid={`edit-node-${index}`}
          className="text-xs text-violet-700 border border-violet-200 rounded px-2 py-1 hover:bg-violet-100 disabled:opacity-40">Configure</button>
        <button type="button" onClick={onRemove} disabled={disabled} aria-label={`Remove ${step.agent}`}
          className="text-xs text-red-600 border border-red-100 rounded px-2 py-1 hover:bg-red-50 disabled:opacity-40">Remove</button>
      </div>
    </div>
  </div>
);

/** The ⊕ insert affordance shown after each trigger stage. */
const InsertButton: React.FC<{ trigger: string; onClick: () => void }> = ({ trigger, onClick }) => (
  <div className="relative flex gap-3 pb-2">
    <div className="relative flex flex-col items-center w-4 shrink-0">
      <span className="flex-1 w-px bg-slate-200" />
    </div>
    <button
      type="button"
      onClick={onClick}
      data-testid={`insert-after-${trigger}`}
      className="flex-1 text-left text-xs text-slate-400 border border-dashed border-slate-300 rounded-lg px-3 py-1.5 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50/40 transition-colors"
    >
      ⊕ Insert custom agent after <span className="font-mono">{trigger}</span>
    </button>
  </div>
);

/** Connector stub between the linear spine and the parallel impl fan. */
const SpineConnector: React.FC = () => (
  <div className="flex gap-3">
    <div className="w-4 flex justify-center"><span className="h-3 w-px bg-slate-200" /></div>
  </div>
);

/** Compact custom-agent card used inside a parallel impl branch column. */
const BranchCustomNode: React.FC<{
  step: WorkflowCustomStep;
  agent?: CustomAgentDef;
  index: number;
  disabled: boolean;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ step, agent, index, disabled, onEdit, onRemove }) => (
  <div className="border border-violet-200 border-l-2 border-l-violet-500 rounded-md bg-violet-50/70 px-2 py-1.5" data-testid={`custom-node-${index}`}>
    <div className="flex items-center justify-between gap-1">
      <span className="font-mono text-xs font-semibold text-violet-900 truncate">{step.agent || '(unnamed)'}</span>
      {step.blocking
        ? <span className="text-[9px] uppercase tracking-wide bg-rose-100 text-rose-700 rounded px-1 py-0.5 shrink-0">blocking</span>
        : <span className="text-[9px] uppercase tracking-wide bg-slate-100 text-slate-500 rounded px-1 py-0.5 shrink-0">advisory</span>}
    </div>
    {agent?.prompt && <div className="text-[10px] text-slate-500 truncate font-mono">{agent.prompt}</div>}
    <div className="flex gap-1 mt-1">
      <button type="button" onClick={onEdit} disabled={disabled} data-testid={`edit-node-${index}`}
        className="text-[10px] text-violet-700 border border-violet-200 rounded px-1.5 py-0.5 hover:bg-violet-100 disabled:opacity-40">Configure</button>
      <button type="button" onClick={onRemove} disabled={disabled} aria-label={`Remove ${step.agent}`}
        className="text-[10px] text-red-600 border border-red-100 rounded px-1.5 py-0.5 hover:bg-red-50 disabled:opacity-40">Remove</button>
    </div>
  </div>
);

/** Compact ⊕ insert affordance inside a parallel impl branch column. */
const BranchInsert: React.FC<{ stage: string; onClick: () => void }> = ({ stage, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`insert-after-${stage}`}
    className="text-left text-[10px] text-slate-400 border border-dashed border-slate-300 rounded-md px-2 py-1 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50/40 transition-colors"
  >
    ⊕ Add agent
  </button>
);

/** Inline "configure" panel — the solo-style step editor. `bare` drops the
 *  vertical rail so it can nest inside the impl-fan band. */
const InlineEditor: React.FC<{
  after: string;
  draft: AgentDraft;
  setDraft: React.Dispatch<React.SetStateAction<AgentDraft>>;
  onCancel: () => void;
  onConfirm: () => void;
  bare?: boolean;
}> = ({ after, draft, setDraft, onCancel, onConfirm, bare }) => {
  const nameOk = /^[a-z][a-z0-9-]{1,40}$/.test(draft.name.trim());
  const promptOk = draft.prompt.trim().length > 0;
  const canConfirm = nameOk && promptOk;
  const set = (k: keyof AgentDraft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [k]: e.target.value }));

  const card = (
      <div className="flex-1 border-2 border-violet-300 rounded-lg bg-white px-3 py-3 space-y-2.5 shadow-sm" data-testid="inline-editor">
        <div className="text-[11px] font-semibold text-violet-700">
          Custom agent · runs after <span className="font-mono">{after}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Field label="name" hint="kebab-case, e.g. security-scanner" error={draft.name && !nameOk ? 'use a-z, 0-9, -' : null}>
            <input data-testid="editor-name" value={draft.name} onChange={set('name')} placeholder="security-scanner"
              className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-violet-400" />
          </Field>
          <Field label="prompt path" hint="file in the target repo">
            <input data-testid="editor-prompt" value={draft.prompt} onChange={set('prompt')} placeholder=".forge/agents/scanner.md"
              className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-violet-400" />
          </Field>
          <Field label="model (optional)">
            <input data-testid="editor-model" value={draft.model} onChange={set('model')} placeholder="claude-haiku-5-5"
              className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-violet-400" />
          </Field>
          <Field label="max iterations (optional)">
            <input data-testid="editor-iter" type="number" min={1} max={120} value={draft.maxIterations} onChange={set('maxIterations')} placeholder="20"
              className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-violet-400" />
          </Field>
        </div>
        <div className="border-t border-slate-100 pt-2">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">on failure</span>
          <div className="flex gap-1.5 mt-1">
            <button type="button" data-testid="editor-advisory"
              onClick={() => setDraft((d) => ({ ...d, blocking: false }))}
              className={'text-xs rounded px-2.5 py-1 border ' + (!draft.blocking ? 'border-violet-400 bg-violet-50 text-violet-700 font-medium' : 'border-slate-300 text-slate-600 hover:bg-slate-50')}>
              Advisory · never blocks
            </button>
            <button type="button" data-testid="editor-blocking"
              onClick={() => setDraft((d) => ({ ...d, blocking: true }))}
              className={'text-xs rounded px-2.5 py-1 border ' + (draft.blocking ? 'border-rose-400 bg-rose-50 text-rose-700 font-medium' : 'border-slate-300 text-slate-600 hover:bg-slate-50')}>
              Blocking · pauses the story
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-0.5">
          <button type="button" onClick={onCancel}
            className="text-xs border border-slate-300 rounded px-3 py-1 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={onConfirm} disabled={!canConfirm}
            data-testid="editor-confirm"
            className="text-xs bg-violet-600 text-white rounded px-3 py-1 hover:bg-violet-700 disabled:opacity-40">Add to pipeline</button>
        </div>
      </div>
  );
  if (bare) return card;
  return (
    <div className="relative flex gap-3 pb-2">
      <div className="relative flex flex-col items-center w-4 shrink-0">
        <span className="mt-1.5 w-3 h-3 rounded-full bg-violet-400 ring-4 ring-violet-50 z-10" />
        <span className="flex-1 w-px bg-slate-200" />
      </div>
      {card}
    </div>
  );
};

const Field: React.FC<{ label: string; hint?: string; error?: string | null; children: React.ReactNode }> = ({
  label, hint, error, children,
}) => (
  <label className="block space-y-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
    {children}
    {error ? <span className="text-[10px] text-red-600">{error}</span>
      : hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}
  </label>
);

export default WorkflowTab;
